<template>
  <div>
    <!-- Input -->
    <div class="wider-box-wrapper mb-4">
      <!-- Rates & Days -->
      <b-card border-variant="secondary" class="border-2" title="Rates &amp; Days">
        <b-row class="mb-3 align-items-center">
          <b-col cols="7" md="9">
            Daily Engagement Contract Rate
            <b-icon
              icon="question"
              variant="primary"
              v-b-tooltip.hover
              title="Assuming you take holiday pay"
            ></b-icon>
          </b-col>
          <b-col cols="5" md="3" class="text-right">£ {{ results.contractRatePerDay.value }}</b-col>
        </b-row>
        <b-row class="mb-3 align-items-center">
          <b-col cols="7" md="9">
            Days Worked
            <b-icon
              icon="question"
              variant="primary"
              v-b-tooltip.hover
              title="Based on a full working year"
            ></b-icon>
          </b-col>
          <b-col cols="5" md="3" class="text-right">{{ results.daysWorked.value }}</b-col>
        </b-row>
      </b-card>
      <div @click="$emit('resetEdit')" class="text-right edit-text-link">Edit</div>
    </div>

    <!-- Output -->
    <div class="output-values wider-box-wrapper">
      <!-- Profit & Loss -->
      <b-card title="Profit &amp; Loss" class="border-2 border-secondary">
        <!-- Group 1 -->
        <b-row class="mb-3 align-items-center">
          <b-col cols="7" md="9">Revenue</b-col>
          <b-col cols="5" md="3" class="text-right">£ {{ results.revenue.value }}</b-col>
        </b-row>
        <b-collapse id="collapse-1" v-model="collapseMap[1]">
          <b-card>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">
                Daily Engagement Contract Rate
                <b-icon
                  icon="question"
                  variant="primary"
                  v-b-tooltip.hover
                  title="Assuming you take holiday pay"
                ></b-icon>
              </b-col>
              <b-col cols="5" md="3" class="text-right">£ {{ results.contractRatePerDay.value }}</b-col>
            </b-row>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">Days Worked</b-col>
              <b-col cols="5" md="3" class="text-right">{{ results.daysWorked.value }}</b-col>
            </b-row>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">Total Revenue</b-col>
              <b-col cols="5" md="3" class="text-right">£ {{ results.revenue.value }}</b-col>
            </b-row>
          </b-card>
        </b-collapse>
        <!-- Group 2 -->
        <b-row class="mb-3 align-items-center">
          <b-col cols="7" md="9">Overheads</b-col>
          <b-col cols="5" md="3" class="text-danger text-right">-£ {{ results.overheads.value }}</b-col>
          <b-icon-document-spreadsheet v-b-toggle.collapse-2></b-icon-document-spreadsheet>
        </b-row>
        <b-collapse id="collapse-2" v-model="collapseMap[2]">
          <b-card>
            <!-- Named expenses - only show if set -->
            <b-row v-if="namedExpenses.computerEquipment " class="mb-3 align-items-center">
              <b-col cols="7" md="9">Computer Equipment</b-col>
              <b-col
                cols="5"
                md="3"
                class="text-danger text-right"
              >-£ {{ namedExpenses.computerEquipment | numFormat('0,0.00') }}</b-col>
            </b-row>
            <b-row v-if="namedExpenses.indemnityInsurance" class="mb-3 align-items-center">
              <b-col cols="7" md="9">Professional Indemnity Insurance</b-col>
              <b-col
                cols="5"
                md="3"
                class="text-danger text-right"
              >-£ {{ namedExpenses.indemnityInsurance | numFormat('0,0.00') }}</b-col>
            </b-row>
            <b-row v-if="namedExpenses.ir35Insurance" class="mb-3 align-items-center">
              <b-col cols="7" md="9">IR35 Insurance</b-col>
              <b-col
                cols="5"
                md="3"
                class="text-danger text-right"
              >-£ {{ namedExpenses.ir35Insurance | numFormat('0,0.00') }}</b-col>
            </b-row>
            <b-row v-if="namedExpenses.publicLiability" class="mb-3 align-items-center">
              <b-col cols="7" md="9">Employee & Public Liability</b-col>
              <b-col
                cols="5"
                md="3"
                class="text-danger text-right"
              >-£ {{ namedExpenses.publicLiability | numFormat('0,0.00') }}</b-col>
            </b-row>
            <b-row v-if="namedExpenses.accountingFees" class="mb-3 align-items-center">
              <b-col cols="7" md="9">Accounting Fees</b-col>
              <b-col
                cols="5"
                md="3"
                class="text-danger text-right"
              >-£ {{ namedExpenses.accountingFees | numFormat('0,0.00') }}</b-col>
            </b-row>
            <!-- Dynamic expense -->
            <div v-for="(expense, key) in otherExpenses" v-bind:key="key">
              <b-row class="mb-3 align-items-center" v-if="expense.amount">
                <b-col cols="7" md="9" v-if="expense.label">{{expense.label}}</b-col>
                <b-col cols="7" md="9" v-else>Additional Expense {{key+1}}</b-col>
                <b-col
                  cols="5"
                  md="3"
                  class="text-danger text-right"
                >-£ {{ expense.amount | numFormat('0,0.00') }}</b-col>
              </b-row>
            </div>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">Total Overheads</b-col>
              <b-col cols="5" md="3" class="text-danger text-right">-£ {{ results.overheads.value }}</b-col>
            </b-row>
          </b-card>
        </b-collapse>
        <!-- Group 3 -->
        <b-row class="mb-3 align-items-center">
          <b-col cols="7" md="9">Staff Costs</b-col>
          <b-col cols="5" md="3" class="text-danger text-right">-£ {{ results.staffCosts.value }}</b-col>
          <b-icon-document-spreadsheet v-b-toggle.collapse-3></b-icon-document-spreadsheet>
        </b-row>
        <b-collapse id="collapse-3" v-model="collapseMap[3]">
          <b-card>
            <h6>Staff Salaries/Pensions</h6>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">Pension Contribution</b-col>
              <b-col
                cols="5"
                md="3"
                class="text-danger text-right"
              >-£ {{ results.pensionCosts.value }}</b-col>
            </b-row>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9" v-if="secondEmployee==='true'">Salaries</b-col>
              <b-col cols="7" md="9" v-else>Contractor Gross Salary</b-col>
              <b-col
                cols="5"
                md="3"
                class="text-danger text-right"
              >-£ {{ results.totalSalaries.value}}</b-col>
            </b-row>
            <h6>Employer NI Due</h6>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">Employer NI Due</b-col>
              <b-col
                cols="5"
                md="3"
                class="text-danger text-right"
              >-£ {{ results.totalNiDue.value }}</b-col>
              <b-icon-document-spreadsheet v-b-toggle.collapse-9></b-icon-document-spreadsheet>
            </b-row>
            <b-collapse id="collapse-9" v-model="collapseMap[9]">
              <b-card no-body>
                <b-card-body>
                  <h6>Employee 1/Contractor</h6>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Contractor Gross Salary</b-col>
                    <!-- employee 1 -->
                    <b-col
                      cols="5"
                      md="3"
                      class="text-right"
                    >£ {{ results.contractorGrossSalary.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Gross Salary up to and Including
                      <abbr
                        v-b-tooltip.hover
                        title="Secondary Threshold"
                      >ST</abbr>
                    </b-col>
                    <!-- employee 1 only -->
                    <b-col
                      cols="5"
                      md="3"
                      v-if="results.contractorGrossPayAboveLowerEarningsLimit.value"
                      class="text-right"
                    >£ {{stringToNumber(results.contractorGrossSalary.value) - stringToNumber(results.contractorGrossPayAboveLowerEarningsLimit.value) | numFormat('0,0.00') }}</b-col>
                    <b-col v-else class="text-right">£ {{results.contractorGrossSalary.value}}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Employer NI Due up to
                      <abbr v-b-tooltip.hover title="Secondary Threshold">ST</abbr>
                    </b-col>
                    <b-col cols="5" md="3" class="text-danger text-right">-£ 0.00</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Gross Salary above
                      <abbr v-b-tooltip.hover title="Secondary Threshold">ST</abbr>
                    </b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-right"
                    >£ {{ results.contractorGrossPayAboveLowerEarningsLimit.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Employer NI Due above
                      <abbr v-b-tooltip.hover title="Secondary Threshold">ST</abbr>
                    </b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-danger text-right"
                    >-£ {{ results.employersNiDueContractor.value}}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Contractor Employer NI Due</b-col>
                    <!-- NOTE: this is employee 1 value only -->
                    <b-col
                      cols="5"
                      md="3"
                      class="text-danger text-right"
                    >-£ {{ results.employersNiDueContractor.value}}</b-col>
                  </b-row>
                </b-card-body>
                <b-card-body v-if="secondEmployee==='true'">
                  <h6>Employee 2/Other</h6>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Other Gross Salary</b-col>
                    <!-- employee 2 -->
                    <b-col cols="5" md="3" class="text-right">£ {{ results.otherGrossSalary.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Gross Salary up to and Including
                      <abbr
                        v-b-tooltip.hover
                        title="Secondary Threshold"
                      >ST</abbr>
                    </b-col>

                    <!-- employee 2 only -->
                    <b-col
                      cols="5"
                      md="3"
                      v-if="results.otherGrossPayAboveLowerEarningsLimit.value"
                      class="text-right"
                    >£ {{stringToNumber(results.otherGrossSalary.value) - stringToNumber(results.otherGrossPayAboveLowerEarningsLimit.value) | numFormat('0,0.00')}}</b-col>
                    <b-col
                      cols="5"
                      md="3"
                      v-else
                      class="text-right"
                    >£ {{results.otherGrossSalary.value}}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Employer NI Due up to
                      <abbr v-b-tooltip.hover title="Secondary Threshold">ST</abbr>
                    </b-col>
                    <b-col cols="5" md="3" class="text-danger text-right">-£ 0.00</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Gross Salary above
                      <abbr v-b-tooltip.hover title="Secondary Threshold">ST</abbr>
                    </b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-right"
                    >£ {{ results.otherGrossPayAboveLowerEarningsLimit.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Employer NI Due above
                      <abbr v-b-tooltip.hover title="Secondary Threshold">ST</abbr>
                    </b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-danger text-right"
                    >-£ {{ results.employersNiDueOther.value}}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Other Employer NI Due</b-col>
                    <!-- NOTE: this is employee 2 value only -->
                    <b-col
                      cols="5"
                      md="3"
                      class="text-danger text-right"
                    >-£ {{ results.employersNiDueOther.value}}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Total Employer NI Due</b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-danger text-right"
                    >-£ {{ results.totalNiDue.value}}</b-col>
                  </b-row>
                </b-card-body>
              </b-card>
            </b-collapse>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">Total Staff Costs</b-col>
              <b-col
                cols="5"
                md="3"
                class="text-danger text-right"
              >-£ {{ results.staffCosts.value }}</b-col>
            </b-row>
          </b-card>
        </b-collapse>
        <b-row class="mb-3 align-items-center">
          <b-col cols="7" md="9">EBIT</b-col>
          <b-col cols="5" md="3" class="text-right">£ {{ results.ebit.value }}</b-col>
        </b-row>
      </b-card>
      <div @click="$emit('resetEdit')" class="text-right edit-text-link">Edit</div>
      <!-- Taxation -->
      <b-card title="Taxation" class="border-2 border-secondary">
        <!-- Group 4 -->
        <b-row class="mb-3 align-items-center">
          <b-col cols="7" md="9">Corporation Tax</b-col>
          <b-col
            cols="5"
            md="3"
            class="text-danger text-right"
          >-£ {{ results.corporationTax.value }}</b-col>
          <b-icon-document-spreadsheet v-b-toggle.collapse-4></b-icon-document-spreadsheet>
        </b-row>
        <b-collapse id="collapse-4" v-model="collapseMap[4]">
          <b-card>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">EBIT</b-col>
              <b-col cols="5" md="3" class="text-right">£ {{ results.ebit.value }}</b-col>
            </b-row>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">Corporation Tax at 19%</b-col>
              <b-col
                cols="5"
                md="3"
                class="text-danger text-right"
              >-£ {{ results.corporationTax.value }}</b-col>
            </b-row>
          </b-card>
        </b-collapse>
        <b-row class="mb-3 align-items-center">
          <b-col cols="7" md="9">Net Profit After Tax</b-col>
          <b-col cols="5" md="3" class="text-right">£ {{ results.netProfitAfterTax.value }}</b-col>
        </b-row>
        <b-row class="mb-3 align-items-center">
          <b-col cols="7" md="9">Dividend at 100% - Based on Single Shareholder</b-col>
          <b-col cols="5" md="3" class="text-right">£ {{ results.netProfitAfterTax.value }}</b-col>
        </b-row>
        <!-- Group 5 -->
        <b-row class="mb-3 align-items-center">
          <b-col cols="7" md="9">Personal Tax on Dividend - Based on Single Shareholder</b-col>
          <b-col
            class="text-danger text-right"
          >-£ {{ results.personalTaxOnDividendBasedonSingleShareholder.value }}</b-col>
          <b-icon-document-spreadsheet v-b-toggle.collapse-5></b-icon-document-spreadsheet>
        </b-row>
        <b-collapse id="collapse-5" v-model="collapseMap[5]">
          <b-card>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">Basic Rate</b-col>
              <b-col
                cols="5"
                md="3"
                class="text-danger text-right"
              >-£ {{ results.dividendTaxBasic.value }}</b-col>
            </b-row>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">Higher Rate</b-col>
              <b-col
                cols="5"
                md="3"
                class="text-danger text-right"
              >-£ {{ results.dividendTaxHigher.value }}</b-col>
            </b-row>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">Additional Rate</b-col>
              <b-col
                cols="5"
                md="3"
                class="text-danger text-right"
              >-£ {{ results.dividendTaxAdditional.value }}</b-col>
            </b-row>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">Total Personal Tax on Dividend - Based on Single Shareholder</b-col>
              <b-col
                class="text-danger text-right"
              >-£ {{ results.personalTaxOnDividendBasedonSingleShareholder.value }}</b-col>
            </b-row>
          </b-card>
        </b-collapse>
      </b-card>
      <!-- Retained -->
      <b-card title="Retained" class="border-2 border-secondary">
        <b-row class="mb-3 align-items-center">
          <b-col cols="7" md="9">Retained Dividend - Based on Single Shareholder</b-col>
          <b-col
            cols="5"
            md="3"
            class="text-right"
          >£ {{ results.retainedDividendBasedOnSingleShareholder.value }}</b-col>
        </b-row>
        <!-- Group 6 -->
        <b-row class="mb-3 align-items-center">
          <b-col
            v-if="secondEmployee==='true'"
            cols="7"
            md="9"
          >Employee Net Salaries (after PAYE and Employee NI)</b-col>
          <b-col v-else cols="7" md="9">Employee Net Salary (after PAYE and Employee NI)</b-col>
          <b-col
            cols="5"
            md="3"
            class="text-right"
          >£ {{ results.employeeNetSalaryAfterEmployeesNi.value }}</b-col>
          <b-icon-document-spreadsheet v-b-toggle.collapse-6></b-icon-document-spreadsheet>
        </b-row>
        <b-collapse id="collapse-6" v-model="collapseMap[6]">
          <b-card>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">Contractor Gross Salary</b-col>
              <b-col cols="5" md="3" class="text-right">£ {{ results.contractorGrossSalary.value }}</b-col>
            </b-row>
            <b-row v-if="secondEmployee==='true'" class="mb-3 align-items-center">
              <b-col cols="7" md="9">Other Salary (e.g. Partner/Company Secretary, Tax Code 1250L)</b-col>
              <b-col cols="5" md="3" class="text-right">£ {{ results.otherGrossSalary.value }}</b-col>
            </b-row>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">Employee PAYE Income Tax Due</b-col>
              <b-col
                cols="5"
                md="3"
                class="text-danger text-right"
              >-£ {{ results.totalPayeTaxDue.value }}</b-col>
              <b-icon-document-spreadsheet v-b-toggle.collapse-7></b-icon-document-spreadsheet>
            </b-row>
            <b-collapse id="collapse-7" v-model="collapseMap[7]">
              <b-card no-body>
                <b-card-body>
                  <!-- Employee 1 -->
                  <h6>Employee 1/Contractor</h6>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Gross Salary</b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-right"
                    >£ {{ results.contractorGrossSalary.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col
                      cols="7"
                      md="9"
                    >Personal Allowance to Include Adjustment for Gross Salary > £100,000</b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-right"
                    >£ {{ results.contractorPersonalAllowanceWithAdjustment.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Gross Salary Liable to PAYE Income Tax</b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-right"
                    >£ {{ results.contractorGrossPayRateliabletoPayeIncomeTax.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Gross Salary Taxable at
                      <abbr v-b-tooltip.hover title="Basic Rate Tax">BRT</abbr>
                    </b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-right"
                    >£ {{ results.contractorGrossPayRateTaxableAtBasicRate.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      PAYE Income Tax Due at
                      <abbr v-b-tooltip.hover title="Basic Rate Tax">BRT</abbr>
                    </b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-danger text-right"
                    >-£ {{ results.contractorPayeIncomeTaxDueAtBasicRate.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Gross Salary Taxable at
                      <abbr v-b-tooltip.hover title="Higher Rate Tax">HRT</abbr>
                    </b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-right"
                    >£ {{ results.contractorGrossPayRateTaxableAtHigherRate.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      PAYE Income Tax Due at
                      <abbr v-b-tooltip.hover title="Higher Rate Tax">HRT</abbr>
                    </b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-danger text-right"
                    >-£ {{ results.contractorPayeIncomeTaxDueAtHigherRate.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Gross Salary Taxable at
                      <abbr v-b-tooltip.hover title="Additional Rate Tax">ART</abbr>
                    </b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-right"
                    >£ {{ results.contractorGrossPayRateTaxableAtAdditionalRate.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      PAYE Income Tax Due at
                      <abbr v-b-tooltip.hover title="Additional Rate Tax">ART</abbr>
                    </b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-danger text-right"
                    >-£ {{ results.contractorPayeIncomeTaxDueAtAdditionalRate.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Contractor PAYE Income Tax Due</b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-danger text-right"
                    >-£ {{ results.contractorTotalPayeIncomeTaxDue.value }}</b-col>
                  </b-row>
                </b-card-body>
                <b-card-body v-if="secondEmployee==='true'">
                  <!-- Employee 2 -->
                  <h6>Employee 2/Other</h6>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Gross Salary</b-col>
                    <b-col cols="5" md="3" class="text-right">£ {{ results.otherGrossSalary.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col
                      cols="7"
                      md="9"
                    >Personal Allowance to Include Adjustment for Gross Salary > £100,000</b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-right"
                    >£ {{ results.otherPersonalAllowanceWithAdjustment.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Gross Salary Liable to PAYE Income Tax</b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-right"
                    >£ {{ results.otherGrossPayRateliabletoPayeIncomeTax.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Gross Salary Taxable at
                      <abbr v-b-tooltip.hover title="Basic Rate Tax">BRT</abbr>
                    </b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-right"
                    >£ {{ results.otherGrossPayRateTaxableAtBasicRate.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      PAYE Income Tax Due at
                      <abbr v-b-tooltip.hover title="Basic Rate Tax">BRT</abbr>
                    </b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-danger text-right"
                    >-£ {{ results.otherPayeIncomeTaxDueAtBasicRate.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Gross Salary Taxable at
                      <abbr v-b-tooltip.hover title="Higher Rate Tax">HRT</abbr>
                    </b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-right"
                    >£ {{ results.otherGrossPayRateTaxableAtHigherRate.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      PAYE Income Tax Due at
                      <abbr v-b-tooltip.hover title="Higher Rate Tax">HRT</abbr>
                    </b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-danger text-right"
                    >-£ {{ results.otherPayeIncomeTaxDueAtHigherRate.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Gross Salary Taxable at
                      <abbr v-b-tooltip.hover title="Additional Rate Tax">ART</abbr>
                    </b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-right"
                    >£ {{ results.otherGrossPayRateTaxableAtAdditionalRate.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      PAYE Income Tax Due at
                      <abbr v-b-tooltip.hover title="Additional Rate Tax">ART</abbr>
                    </b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-danger text-right"
                    >-£ {{ results.otherPayeIncomeTaxDueAtAdditionalRate.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Other PAYE Income Tax Due</b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-danger text-right"
                    >-£ {{ results.otherTotalPayeIncomeTaxDue.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Total PAYE Income Tax Due</b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-danger text-right"
                    >-£ {{ results.totalPayeTaxDue.value }}</b-col>
                  </b-row>
                </b-card-body>
              </b-card>
            </b-collapse>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">Total Employee NI Due</b-col>
              <b-col
                cols="5"
                md="3"
                class="text-danger text-right"
              >-£ {{ results.totalAllEmployeesNi.value }}</b-col>
              <b-icon-document-spreadsheet v-b-toggle.collapse-8></b-icon-document-spreadsheet>
            </b-row>
            <b-collapse id="collapse-8" v-model="collapseMap[8]">
              <b-card no-body>
                <b-card-body>
                  <!-- Employee 1 -->
                  <h6>Employee 1/Contractor</h6>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Gross Salary</b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-right"
                    >£ {{ results.contractorGrossSalary.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Gross Salary at or above
                      <abbr
                        v-b-tooltip.hover
                        title="Lower Earnings Limit"
                      >LEL</abbr> up to and Including
                      <abbr v-b-tooltip.hover title="Primary Threshold">PT</abbr>
                    </b-col>
                    <b-col
                      class="text-right"
                    >£ {{ results.contractorGrossPayRateAboveLowerEarningsLimitToPrimaryThreshold.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Employee NI Due up to
                      <abbr v-b-tooltip.hover title="Primary Threshold">PT</abbr>
                    </b-col>
                    <b-col
                      class="text-danger text-right"
                    >-£ {{ results.contractorNiDueUpToPrimaryThreshold.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Gross Salary above the
                      <abbr v-b-tooltip.hover title="Primary Threshold">PT</abbr> up to and Including
                      <abbr v-b-tooltip.hover title="Upper Earnings Limit">UEL</abbr>
                    </b-col>
                    <b-col
                      class="text-right"
                    >£ {{ results.contractorGrossPayRateAbovePrimaryThresholdToUpperEarningLimit.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Employee NI Due above the
                      <abbr v-b-tooltip.hover title="Primary Threshold">PT</abbr> up to and Including
                      <abbr v-b-tooltip.hover title="Upper Earnings Limit">UEL</abbr>
                    </b-col>
                    <b-col
                      class="text-danger text-right"
                    >-£ {{ results.contractorNiDueAbovePrimaryThresholdToUpperEarningLimit.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Gross Salary above
                      <abbr v-b-tooltip.hover title="Upper Earnings Limit">UEL</abbr>
                    </b-col>
                    <b-col
                      class="text-right"
                    >£ {{ results.contractorGrossPayRateAboveUpperEarningLimit.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Employee NI Due above
                      <abbr v-b-tooltip.hover title="Upper Earnings Limit">UEL</abbr>
                    </b-col>
                    <b-col
                      class="text-danger text-right"
                    >-£ {{ results.contractorNiDueAboveUpperEarningLimit.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Contractor Employee NI Due</b-col>
                    <b-col
                      class="text-danger text-right"
                    >-£ {{ results.contractorTotalEmployeeNiDue.value }}</b-col>
                  </b-row>
                </b-card-body>
                <b-card-body v-if="secondEmployee==='true'">
                  <!-- Employee 2 -->
                  <h6>Employee 2/Other</h6>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Gross Salary</b-col>
                    <b-col cols="5" md="3" class="text-right">£ {{ results.otherGrossSalary.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Gross Salary at or above
                      <abbr
                        v-b-tooltip.hover
                        title="Lower Earnings Limit"
                      >LEL</abbr> up to and Including
                      <abbr v-b-tooltip.hover title="Primary Threshold">PT</abbr>
                    </b-col>
                    <b-col
                      class="text-right"
                    >£ {{ results.otherGrossPayRateAboveLowerEarningsLimitToPrimaryThreshold.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Employee NI Due up to
                      <abbr v-b-tooltip.hover title="Primary Threshold">PT</abbr>
                    </b-col>
                    <b-col
                      class="text-danger text-right"
                    >-£ {{ results.otherNiDueUpToPrimaryThreshold.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Gross Salary above the
                      <abbr v-b-tooltip.hover title="Primary Threshold">PT</abbr> up to and Including
                      <abbr v-b-tooltip.hover title="Upper Earnings Limit">UEL</abbr>
                    </b-col>
                    <b-col
                      class="text-right"
                    >£ {{ results.otherGrossPayRateAbovePrimaryThresholdToUpperEarningLimit.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Employee NI Due above the
                      <abbr v-b-tooltip.hover title="Primary Threshold">PT</abbr> up to and Including
                      <abbr v-b-tooltip.hover title="Upper Earnings Limit">UEL</abbr>
                    </b-col>
                    <b-col
                      class="text-danger text-right"
                    >-£ {{ results.otherNiDueAbovePrimaryThresholdToUpperEarningLimit.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Gross Salary above
                      <abbr v-b-tooltip.hover title="Upper Earnings Limit">UEL</abbr>
                    </b-col>
                    <b-col
                      class="text-right"
                    >£ {{ results.otherGrossPayRateAboveUpperEarningLimit.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">
                      Employee NI Due above
                      <abbr v-b-tooltip.hover title="Upper Earnings Limit">UEL</abbr>
                    </b-col>
                    <b-col
                      class="text-danger text-right"
                    >-£ {{ results.otherNiDueAboveUpperEarningLimit.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Other Employee NI Due</b-col>
                    <b-col
                      class="text-danger text-right"
                    >-£ {{ results.otherTotalEmployeeNiDue.value }}</b-col>
                  </b-row>
                  <b-row class="mb-3 align-items-center">
                    <b-col cols="7" md="9">Total Employee NI Due</b-col>
                    <b-col
                      cols="5"
                      md="3"
                      class="text-danger text-right"
                    >-£ {{ results.totalAllEmployeesNi.value }}</b-col>
                  </b-row>
                </b-card-body>
              </b-card>
            </b-collapse>
            <b-row class="mb-3 align-items-center">
              <b-col cols="7" md="9">Total Net Salary</b-col>
              <b-col
                cols="5"
                md="3"
                class="text-right"
              >£ {{ results.employeeNetSalaryAfterEmployeesNi.value }}</b-col>
            </b-row>
          </b-card>
        </b-collapse>
        <b-row class="mb-3 align-items-center">
          <b-col cols="7" md="9">Pension and Other Staff Costs Retained</b-col>
          <b-col cols="5" md="3" class="text-right">£ {{ results.staffCostsRetained.value }}</b-col>
        </b-row>
        <b-row class="mb-3 align-items-center">
          <b-col cols="7" md="9">Total Retained</b-col>
          <b-col cols="5" md="3" class="text-right">£ {{ results.totalRetained.value }}</b-col>
        </b-row>
      </b-card>
    </div>
    <!-- Summary -->
    <div class="wider-box-wrapper summary-box">
      <b-card title="Summary" border-variant="primary" class="border-2">
        <b-row class="mb-3 align-items-center">
          <b-col cols="7" md="9">Effective Daily Net</b-col>
          <b-col cols="5" md="3" class="text-right">£ {{ results.effectiveDailyNet.value }}</b-col>
        </b-row>
        <b-row class="mb-3 align-items-center">
          <b-col cols="7" md="9">Net Retention %</b-col>
          <b-col cols="5" md="3" class="text-right">{{ results.netRetention.value }} %</b-col>
        </b-row>
      </b-card>
    </div>
    <div class>
      <b-form>
        <b-button @click="nextStep" variant="primary">Go to Step 3</b-button>
      </b-form>
    </div>
  </div>
</template>

<script>
import numeral from "numeral";

export default {
  props: ["results", "secondEmployee"],
  data() {
    return {
      collapseMap: {
        1: false,
        2: false,
        3: false,
        4: false,
        5: false,
        6: false,
        7: false,
        8: false,
        9: false
      },
      otherExpenses: this.$store.state.otherExpenses
    };
  },
  computed: {
    expandAll() {
      return this.$store.state.expandCollapse.two;
    },
    namedExpenses() {
      return this.$store.state.namedExpenses;
    }
  },
  watch: {
    expandAll(event) {
      if (event.active) {
        if (event.value) {
          this.expand();
        } else {
          this.collapse();
        }
        this.$store.commit("expandCollapseAll", {
          calculator: "two",
          action: "reset"
        });
      }
    }
  },
  methods: {
    expand: function() {
      Object.keys(this.collapseMap).forEach(key => {
        this.collapseMap[key] = true;
      });
    },
    collapse: function() {
      Object.keys(this.collapseMap).forEach(key => {
        this.collapseMap[key] = false;
      });
    },
    nextStep: function() {
      this.$store.commit("prepopulateField", {
        calculator: "three",
        value: this.results.effectiveDailyNet.value
      });
      this.$emit("incrementTab");
    },
    stringToNumber: function(string) {
      return numeral(string).value();
    }
  }
};
</script>
